import Promise from 'bluebird';
import _ from 'lodash';

import api from '../api';
import filters from '../core/filters';
import { stripMarketShare } from '../core/tools/brand';
import dialogBox from '../core/dialog';
import ColorBrewer from '../core/tools/colorbrewer';
import colorSwatch from '../core/tools/color-swatch';
import translate from '../core/tools/translate';
import environment from '../environment';
import { notify, NOTIFY_TYPE } from '../shared/notify';
import ShapesControl from './ShapesControl';
import { Constants } from '../../constants/constants';
import infoBoard from '../core/infoboard';
import worldMap from '../core/map';

const isAuthorized = (permission, feature) => api.authorization.isAuthorized(permission, feature);

const _getMarkedAreaThemes = () => {
    const markedThemes = (environment.get('CONFIG.FILTERS.MARKED_AREA_THEMES') || []);

    const shapeThemes = Object.keys(markedThemes).reduce((acc, curr) => {
        if (markedThemes[curr].id === Constants.DNC_FLEXIBLE) {
            return acc;
        }

        acc[curr] = markedThemes[curr];
        return acc;
    }, {});

    return shapeThemes;
};

export class MarkedAreaControl extends ShapesControl {
    constructor() {
        const options = {
            id: 'operational-area',
            name: 'MarkedArea',
            types: environment.get('CONFIG.FILTERS.MARKED_AREA_TYPES'),
            themes: _getMarkedAreaThemes(),
            request: api.themes.get.area.sales,
            filter: true,
            overview: true
        };

        super(options);
        this.colorize = this.colorize.bind(this);
        this._toggleAdditionalArea = this._toggleAdditionalArea.bind(this);
        this._changeAdditionalArea = this._changeAdditionalArea.bind(this);
    }

    static getInstance() {
        if (!MarkedAreaControl._instance) {
            MarkedAreaControl._instance = new MarkedAreaControl();
        }

        return MarkedAreaControl._instance;
    }

    colorize(featureCollection, layer) {
        return new Promise(resolve => {
            // if (this.getTypeValue() === environment.get('TYPE.AREACONTRATUAL')) {
            //     notify(NOTIFY_TYPE.ERROR, translate('Validation.UnavailableOnlyFor', { location: 'Área Contratual' }));
            //     return resolve(featureCollection);
            // }
            this.setThemeLayerOptions();

            super.colorize(featureCollection, layer)
              .then(res => {
                  if (this._checkThemes(['openPoint'])) {
                      _.forEach(res.features, f => {
                          if (_.isNumber(f.properties.Total)) {
                                const total = Number(f.properties.RegistryDetail[0].totalIndustry)
                                    .toLocaleString('pt-BR');

                                f.properties.Status = f.properties.Total > 0 ? 'Nominated' : 'Open Point';
                                f.properties.Total = f.properties.Total > 0
                                    ? `Nominated - ${total}`
                                    : `Open Point - ${total}`;
                          }
                      });
                  }

                  if (this._checkThemes(['locationName'])) {
                      _.forEach(res.features, f => {
                          f.properties.Total = f.properties.Nome;
                      });
                  }

                  if (this._checkThemes(['groupsArea', 'brandArea', 'segmentLeader'])) {
                       _.forEach(res.features, f => {
                          if (f.properties.Total === 0) {
                                f.properties.Brand = 0;
                                return f.properties.notPaint = true;
                          }

                          f.properties.notPaint = false;
                          if (f.properties.RegistryDetail.length > 1) {
                                f.properties.Total = f.properties.RegistryDetail
                                    .map(r => _.upperFirst(_.toLower(_.trim(r.valor))))
                                    .join(" + ");
                                f.properties.Brand = f.properties.RegistryDetail
                                    .map(r => stripMarketShare(_.trim(r.valor)))
                                    .join(" + ");

                                f.properties.isComposedGroup = true;
                          } else {
                                f.properties.Brand = stripMarketShare(f.properties.Total);
                          }
                      });
                  }

                  resolve(res);
            });
        });
    }

    setThemeLayerOptions() {
        const options = this.getLayer().options;
        options.value = 'Total';
        if (this._checkThemes(['openPoint', 'locationName', 'groupsArea', 'brandArea', 'segmentLeader'])) {
            this.getControls().button.filter.input.enable(false);
            this.getControls().button.filter.enable = false;
            this.getControls().slider.range.input.enable(false);
            this.getControls().slider.range.enable = false;

            if (this._checkThemes(['brandArea', 'segmentLeader'])) {
                this.getControls().colorpicker.fillColor.input.enable(false);
                this.getControls().colorpicker.fillColor.enable = false;
                this.getControls().button.overview.input.enable(true);
                this.getControls().button.overview.enable = true;
                options.value = 'Brand';
                options.symbology.scale = 'nomenal';
                options.symbology.theme = this._getAreaThemePalette();
                options.symbology.defaultColor = 'rgb(204,204,204)';
            } else if (this._checkThemes(['openPoint'])) {
                this.getControls().colorpicker.fillColor.input.enable(false);
                this.getControls().colorpicker.fillColor.enable = false;
                options.value = 'Status';
                options.symbology.scale = 'nomenal';
                options.symbology.theme = ColorBrewer.openPoint;
                options.symbology.defaultColor = 'rgb(204,204,204)';
            } else {
                this.getControls().colorpicker.fillColor.input.enable(true);
                this.getControls().colorpicker.fillColor.enable = true;
                this.getControls().colorpicker.fillColor.input.value('Set2:6');
                this.getControls().colorpicker.fillColor.value = 'Set2:6';
                options.symbology.scale = 'nomenal';
                options.symbology.theme = ColorBrewer.Set2['6'];
            }

            infoBoard.toggleDataboard(false, 0);
        } else {
            this.getControls().colorpicker.fillColor.input.enable(true);
            this.getControls().colorpicker.fillColor.enable = true;
            options.symbology.scale = 'jenks';
            options.symbology.theme = ColorBrewer.OrRd[9];
        }
        this.getLayer().setOptions(options);
    }

    _resetAdditionalArea() {
        const layer = this.getLayer(worldMap.getCurrent(), 'MarkedAreaAdditional');

        layer.hide();
        layer.options.remote.params.locationType = 0;
        layer.options.remote.params.overlap = undefined;
        layer.setOptions({ legend: false });
    }

    _toggleAdditionalArea(checked) {
        const layer = this.getLayer(worldMap.getCurrent(), 'MarkedAreaAdditional');

        if (!checked) {
            this._resetAdditionalArea();
            return;
        }

        if (!this.check() || !this.getLayer().visible()) {
            notify(NOTIFY_TYPE.LOG, translate('Validation.EnableAdditionalArea'));
            _.defer(() => this.getControls().checkbox.additionalArea.input.iCheck('uncheck'));
            return;
        }

        this._selectAdditionalArea(layer, () => this.getControls().checkbox.additionalArea.input.iCheck('uncheck'));
    }

    _change(e) {
        if (!e.checked) {
            this._resetAdditionalArea();
            _.defer(() => {
                try {
                    this.getControls().checkbox.additionalArea.input.iCheck('uncheck');
                } catch (err) {
                    console.log(err);
                }
            });
        }

        return super._change(e);
    }

    _changeAdditionalArea() {
        const checkbox = this.getControls().checkbox.additionalArea;

        if (!checkbox.status) {
            checkbox.input.iCheck('check');
            return;
        }

        this._selectAdditionalArea(this.getLayer(worldMap.getCurrent(), 'MarkedAreaAdditional'), () => {});
    }

    _selectAdditionalArea(layer, onCancel) {
        const typeId = this.getTypeValue(true);

        dialogBox.additionalArea({
            types: _(this.options.types)
                .filter((type, key) => key !== 'DINAMICO' && type.id !== typeId)
                .map(type => ({ text: type.name, value: type.id }))
                .value(),
            value: {
                locationType: layer.options.remote.params.locationType,
                color: layer.options.symbology.style.color,
                weight: layer.options.symbology.style.weight,
                tag: !!layer.options.tag
            },
            confirmed: layer.visible(),
            onChangeType: locationType => this._setAdditionalAreaType(layer, locationType),
            onChangeStyle: style => this._setAdditionalAreaStyle(layer, style),
            onToggleTag: isChecked => this._toggleAdditionalAreaTag(layer, isChecked),
            onCancel
        });
    }

    _setAdditionalAreaType(layer, locationType) {
        layer.options.remote.params.locationType = locationType;
        layer.setOptions({ legend: this._getAdditionalAreaLegend(layer) });
        layer.reset();
    }

    _getAdditionalAreaLegend(layer) {
        const type = _.find(
            this.options.types,
            t => t.id === Number(layer.options.remote.params.locationType)
        );

        if (!type) {
            return false;
        }

        const swatch = colorSwatch(layer.options.symbology.style.color, 20, 20);

        return {
            title: translate('Common.AdditionalArea'),
            subtitle: '',
            icon: () => swatch,
            text: () => type.name
        };
    }

    _getAdditionalAreaTag(layer) {
        return {
            property: 'Nome',
            box: true,
            size: 9,
            offsetY: -5,
            bold: true,
            colorize: true,
            color: layer.options.symbology.style.color
        };
    }

    _setAdditionalAreaStyle(layer, style) {
        const { symbology } = layer.options;

        symbology.style = _.assign({}, symbology.style, style);
        layer.setOptions({ symbology });

        if (layer.options.tag) {
            layer.setOptions({ tag: this._getAdditionalAreaTag(layer) });
        }

        if (layer.options.legend) {
            layer.setOptions({ legend: this._getAdditionalAreaLegend(layer) });
        }

        if (layer.visible()) {
            layer.reload();
        }
    }

    _toggleAdditionalAreaTag(layer, isChecked) {
        layer.setOptions({ tag: isChecked ? this._getAdditionalAreaTag(layer) : undefined });

        if (layer.visible()) {
            layer.reload();
        }
    }
}

export default MarkedAreaControl;



// WEBPACK FOOTER //
// ./src/scripts/controls/MarkedAreaControl.js